// WhatsNew — what version am I on, and what changed?
//
// Reported by a worker through the in-app form: "can't see the changes made to
// each version or version im on". The classic app answers it at
// components/WhatsNew; the rebuilt apps showed the version as a row on Me and
// had nowhere at all to read what changed.
//
// ── Where the content comes from (nothing is generated here) ────────────────
//
//   the version   client/src/version.js — the SAME module the classic app
//                 imports, so the two can never disagree. It is a build-time
//                 constant, which is why it is still right when the fetch
//                 below fails.
//   the changes   /changelog.json, written into the build by
//                 .github/workflows/deploy.yml (`scripts/release-notes.js
//                 --out client/build/changelog.json`) from the `What's new:`
//                 lines authors wrote in the commits since the previous
//                 deploy tag. Served by nginx as a static file — no endpoint, so
//                 plain fetch rather than shared/http.js, which prefixes /api.
//
// So the content IS shared with the classic screen: both read one build
// artefact and one module. Only the presentation differs (fw Sheet, not the
// classic Modal), which is the whole point of the rebuild.
//
// ── Three rules that are easy to get backwards ──────────────────────────────
//
//   - **an empty list is a real answer, not a failure.** deploy.yml
//     deliberately writes `items: []` when nobody wrote a line for a person
//     to read, precisely so this panel does not say "internal
//     improvements" every time and teach people to stop reading it. Folding
//     the empty case into the error case replaces a true statement with a
//     false one.
//   - **a failed fetch must not take the version down with it.**
//     /changelog.json 404s on any install deployed before it existed, and on
//     any nginx not taught to serve it. The version is still correct then.
//   - **nothing is fetched while the sheet is closed.** These sheets are
//     mounted permanently by the screen that owns them, so closing one does
//     not unmount an open dialog; without the guard every load of that screen
//     fetches a file nobody asked for.

import React, { useEffect, useState } from 'react';
import { Sheet, FwButton, Notice, Skeleton, SectionLabel } from '../../ui/fw';
import APP_VERSION from '../../version';

/** Static, at the site root — not an API path. */
export const CHANGELOG_URL = '/changelog.json';

/**
 * The lines to show, out of whatever /changelog.json turned out to contain.
 *
 * Always an array. A missing or non-array `items` is "nothing to show", not a
 * crash: an older changelog, or a proxy handing back an index.html body that
 * happens to parse, both arrive here and `.map` on a non-array throws during
 * render. Non-string entries are dropped rather than rendered — React throws
 * on an object child, so one malformed line would blank the whole sheet.
 */
export function changelogItems(payload) {
  const items = payload && payload.items;
  if (!Array.isArray(items)) return [];
  return items.filter((x) => typeof x === 'string' && x.trim()).map((x) => x.trim());
}

/**
 * @param {boolean}  open
 * @param {Function} onClose
 * @param {Function} [onReport]  called with ONE change line when the person
 *   taps report beside it. Optional on purpose: the sheet is rendered by the
 *   Me screen, and a caller that does not wire it up gets exactly the old
 *   sheet rather than a button that does nothing.
 */
export default function WhatsNew({ open, onClose, onReport }) {
  const [notes, setNotes] = useState(null);   // null = not fetched yet
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open || notes !== null) return undefined;
    let cancelled = false;
    // cache: 'no-store' because a stale service worker will otherwise hand
    // back the PREVIOUS build's changelog — which is exactly the list the
    // person has just stopped running.
    fetch(CHANGELOG_URL, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d) => { if (!cancelled) setNotes(changelogItems(d)); })
      .catch(() => { if (!cancelled) { setNotes([]); setFailed(true); } });
    return () => { cancelled = true; };
  }, [open, notes]);

  return (
    <Sheet
      open={open}
      title="What's new"
      onClose={onClose}
      footer={<div className="fw-jobform__footer"><FwButton variant="primary" onClick={onClose}>Close</FwButton></div>}
    >
      <div className="fw-whatsnew">
        <p className="fw-whatsnew__version">
          You are running <strong className="fw-num">v{APP_VERSION}</strong>
        </p>

        {notes === null ? <Skeleton lines={3} /> : null}

        {notes !== null && notes.length > 0 ? (
          <>
            <SectionLabel>Changed in this version</SectionLabel>
            <ul className="fw-whatsnew__list">
              {notes.map((item, i) => (
                <li key={i}>
                  <span className="fw-whatsnew__item">{item}</span>
                  {/* One tap from a change to a report that NAMES it (#53).
                      The version has always travelled with a report —
                      utils/diagnostics.js puts `clientVersion` in every one —
                      so what was missing was which change it is about. The
                      accessible name repeats the line because five identical
                      "Report" buttons in a list is a control a screen reader
                      cannot tell apart, and a thumb cannot either once the
                      list has scrolled. */}
                  {onReport ? (
                    <button
                      type="button"
                      className="fw-whatsnew__report"
                      data-report-item
                      aria-label={`Report a problem with: ${item}`}
                      onClick={() => onReport(item)}
                    >
                      Report
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          </>
        ) : null}

        {notes !== null && notes.length === 0 && !failed ? (
          <p className="fw-whatsnew__muted">
            Nothing you would notice changed in this version — the last release was
            behind-the-scenes work.
          </p>
        ) : null}

        {failed ? (
          <Notice tone="info" title="Could not load the list of changes">
            The version above is still correct — it comes from the app on this phone, not
            from the network.
          </Notice>
        ) : null}

        <p className="fw-whatsnew__muted">
          {onReport
            ? 'If one of these looks wrong, tap Report beside it — the report carries the version you are on.'
            : 'If something looks wrong right after an update, say so and mention the screen. That is usually enough to find it.'}
        </p>
      </div>
    </Sheet>
  );
}
